import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import { useNavigate, useSearchParams } from 'react-router-dom';
import sessionCheck from "../session/userData";
import { encryptDataHybrid } from "../enc/useEncrypt";
import { getDeviceInfo } from '../utils/deviceInfo';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Papa from 'papaparse';
import {
    DocumentArrowDownIcon,
    EyeIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    MagnifyingGlassIcon,
    ChevronLeftIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import { TbFilterPause } from "react-icons/tb";
import { Bar, Pie } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable'; // This adds the autoTable plugin to jsPDF
import { motion, AnimatePresence } from 'framer-motion';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
);

const API_URL = process.env.REACT_APP_API_URL;
const APP_NAME = process.env.REACT_APP_NAME;
const TRADE_NAME = process.env.REACT_APP_TRADE_NAME;
const CONTACT_EMAIL = process.env.REACT_APP_CONTACT_EMAIL;
const CONTACT_NUMBER = process.env.REACT_APP_CONTACT_NUMBER;
const CONTACT_ADDRESS = process.env.REACT_APP_CONTACT_ADDRESS;
const GST_NUMBER = process.env.REACT_APP_GST_NUMBER;
const WEBSITE = process.env.REACT_APP_WEB_URL;

// Skeleton Loading Components
const TableSkeleton = () => (
    <div className="animate-pulse">
        <div className="h-8 bg-gray-200 dark:bg-gray-600 rounded mb-4"></div>
        {[...Array(5)].map((_, i) => (
            <div key={i} className="h-12 bg-gray-100 dark:bg-gray-700 rounded mb-2"></div>
        ))}
    </div>
);

const CardSkeleton = () => (
    <div className="animate-pulse bg-gray-200 dark:bg-gray-700 rounded-lg p-6 h-32"></div>
);

const UserReport = () => {
    const [searchParams] = useSearchParams();
    const userId = searchParams.get('userId');
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [ledgerData, setLedgerData] = useState([]);
    const [summaryData, setSummaryData] = useState(null);
    const [access_data, setAccess_data] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
    const navigate = useNavigate();

    // Get today's date in YYYY-MM-DD format
    const getTodayDate = () => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    };

    const [filters, setFilters] = useState({
        userId: userId || '',
        fromDate: getTodayDate(),
        toDate: getTodayDate()
    });

    useEffect(() => {
        if (sessionCheck() == null) {
            navigate("../login", { replace: true });
        } else {
            setAccess_data(JSON.parse(sessionCheck()));
        }
    }, []);

    useEffect(() => {
        if (access_data?.username) {
            fetchLedgerData();
        }
    }, [access_data?.username]);

    useEffect(() => {
        if (filterDrawerOpen) {
            // disable scrolling
            document.body.style.overflow = "hidden";
        } else {
            // enable scrolling again
            document.body.style.overflow = "auto";
        }

        // cleanup when component unmounts
        return () => {
            document.body.style.overflow = "auto";
        };
    }, [filterDrawerOpen]);

    const fetchLedgerData = async () => {
        setLoading(true);
        try {
            const dataToEncrypt = {
                user_id: access_data?.username || '',
                login_id: access_data?.login_id || '',
                device_id: getDeviceInfo().deviceId,
                payload: {
                    user_id: filters.userId,
                    from_date: filters.fromDate,
                    to_date: filters.toDate
                }
            };

            const encryptedMessage = encryptDataHybrid(dataToEncrypt);

            if (!encryptedMessage) {
                toast.error("Encryption failed. Please try again.");
                setLoading(false);
                return;
            }

            const response = await fetch(`${API_URL}user-report.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
                },
                body: JSON.stringify({
                    encrypted_key: encryptedMessage.encryptedKey,
                    encrypted_data: encryptedMessage.encryptedData,
                }),
            });

            const result = await response.json();

            if (result.status === true) {
                setLedgerData(result.data || []);
                calculateSummary(result.data || []);
            } else {
                toast.error(result.message || "Failed to fetch ledger data.");
                setLedgerData([]);
                setSummaryData(null);
            }
        } catch (error) {
            console.error("Fetch ledger error:", error);
            toast.error("An unexpected error occurred. Please try again.");
            setLedgerData([]);
            setSummaryData(null);
        } finally {
            setLoading(false);
        }
    };

    const calculateSummary = (data) => {
        if (!data || data.length === 0) {
            setSummaryData(null);
            return;
        }

        const summary = {
            openingBalance: 0,
            closingBalance: 0,
            totalDebit: 0,
            totalCredit: 0,
            recharge: {
                count: 0,
                amount: 0
            },
            billPayment: {
                count: 0,
                amount: 0
            },
            fundTransfer: {
                count: 0,
                amount: 0
            },
            otherTransactions: {
                count: 0,
                amount: 0
            },
            categories: {}
        };

        // Get opening and closing balance
        if (data.length > 0) {
            summary.openingBalance = parseFloat(data[data.length - 1].balance) +
                parseFloat(data[data.length - 1].dr_amount) -
                parseFloat(data[data.length - 1].cr_amount);
            summary.closingBalance = parseFloat(data[0].balance);
        }

        // Calculate totals and category-wise data
        data.forEach(entry => {
            const amount = parseFloat(entry.dr_amount !== "0" ? entry.dr_amount : entry.cr_amount);

            if (entry.dr_amount !== "0") {
                summary.totalDebit += amount;
            } else {
                summary.totalCredit += amount;
            }

            // Categorize transactions
            switch (entry.category) {
                case "2": // Mobile Recharge
                    summary.recharge.count++;
                    summary.recharge.amount += amount;
                    break;
                case "3": // Bill Payment
                    summary.billPayment.count++;
                    summary.billPayment.amount += amount;
                    break;
                case "9": // Credit Transfer
                case "10": // Debit Transfer
                    summary.fundTransfer.count++;
                    summary.fundTransfer.amount += amount;
                    break;
                default:
                    summary.otherTransactions.count++;
                    summary.otherTransactions.amount += amount;
            }

            // Count by category
            const categoryName = getCategory(entry.category) || 'Other';
            if (!summary.categories[categoryName]) {
                summary.categories[categoryName] = {
                    count: 0,
                    amount: 0
                };
            }
            summary.categories[categoryName].count++;
            summary.categories[categoryName].amount += amount;
        });

        setSummaryData(summary);
    };

    const toggleMobileSidebar = () => {
        setMobileSidebarOpen(!mobileSidebarOpen);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        fetchLedgerData();
    };

    const handleDownloadPDF = async () => {
        if (ledgerData.length === 0) {
            toast.warning("No data available to download");
            return;
        }

        toast.info("Generating PDF report...");

        try {
            // Create a new PDF document
            const pdf = new jsPDF();

            // Add company header
            pdf.setFontSize(18);
            pdf.setTextColor(40);
            pdf.text(TRADE_NAME, 15, 15);

            pdf.setFontSize(12);
            pdf.text(`User Report for ${filters.userId}`, 15, 25);
            pdf.text(`From ${filters.fromDate} to ${filters.toDate}`, 15, 35);

            // Add current date
            pdf.text(`Generated on: ${new Date().toLocaleString()}`, 15, 45);

            // Add a line separator
            pdf.line(15, 50, pdf.internal.pageSize.width - 15, 50);

            // Add summary cards
            pdf.setFontSize(14);
            pdf.text('Summary Overview', 15, 60);

            let yPosition = 70;

            // Add summary data
            if (summaryData) {
                // Create summary table
                const summaryTableData = [
                    ['Opening Balance', `₹${summaryData.openingBalance.toFixed(2)}`],
                    ['Closing Balance', `₹${summaryData.closingBalance.toFixed(2)}`],
                    ['Total Credit', `₹${summaryData.totalCredit.toFixed(2)}`],
                    ['Total Debit', `₹${summaryData.totalDebit.toFixed(2)}`],
                    ['Recharge Transactions', `${summaryData.recharge.count} (₹${summaryData.recharge.amount.toFixed(2)})`],
                    ['Bill Payments', `${summaryData.billPayment.count} (₹${summaryData.billPayment.amount.toFixed(2)})`],
                    ['Fund Transfers', `${summaryData.fundTransfer.count} (₹${summaryData.fundTransfer.amount.toFixed(2)})`]
                ];

                pdf.autoTable({
                    startY: yPosition,
                    head: [['Metric', 'Value']],
                    body: summaryTableData,
                    margin: { left: 15, right: 15 },
                    styles: { fontSize: 10 },
                    headStyles: { fillColor: [41, 128, 185], textColor: 255 }
                });

                yPosition = pdf.lastAutoTable.finalY + 15;
            }

            // Add transaction chart
            pdf.setFontSize(14);
            pdf.text('Transaction Distribution', 15, yPosition);
            yPosition += 10;

            // Generate chart image and add to PDF
            const chartContainer = document.getElementById('transaction-chart');
            if (chartContainer) {
                const canvas = await html2canvas(chartContainer);
                const imgData = canvas.toDataURL('image/png');

                // Calculate dimensions to fit width
                const imgWidth = pdf.internal.pageSize.width - 30;
                const imgHeight = (canvas.height * imgWidth) / canvas.width;

                pdf.addImage(imgData, 'PNG', 15, yPosition, imgWidth, imgHeight);
                yPosition += imgHeight + 15;
            }

            // Add category chart
            pdf.setFontSize(14);
            pdf.text('Transaction Categories', 15, yPosition);
            yPosition += 10;

            const categoryChartContainer = document.getElementById('category-chart');
            if (categoryChartContainer) {
                const canvas = await html2canvas(categoryChartContainer);
                const imgData = canvas.toDataURL('image/png');

                // Calculate dimensions to fit width
                const imgWidth = pdf.internal.pageSize.width - 30;
                const imgHeight = (canvas.height * imgWidth) / canvas.width;

                pdf.addImage(imgData, 'PNG', 15, yPosition, imgWidth, imgHeight);
                yPosition += imgHeight + 15;
            }

            // Add transaction table
            pdf.setFontSize(14);
            pdf.text('Transaction Details', 15, yPosition);
            yPosition += 10;

            // Prepare table data
            const tableColumns = ["Date", "Order ID", "Purpose", "Debit", "Credit", "Balance"];
            const tableRows = ledgerData.map(entry => [
                entry.createAt,
                entry.order_id,
                `${getCategory(entry.category)} | ${entry.number}`.substring(0, 30), // Limit length
                entry.dr_amount !== "0" ? `₹${entry.dr_amount}` : '-',
                entry.cr_amount !== "0" ? `₹${entry.cr_amount}` : '-',
                `₹${entry.balance}`
            ]);

            // Add table to PDF
            pdf.autoTable({
                startY: yPosition,
                head: [tableColumns],
                body: tableRows,
                margin: { left: 15, right: 15 },
                styles: { fontSize: 8, cellPadding: 2 },
                headStyles: { fillColor: [41, 128, 185], textColor: 255 },
                columnStyles: {
                    0: { cellWidth: 25 }, // Date
                    1: { cellWidth: 20 }, // Order ID
                    2: { cellWidth: 35 }, // Purpose
                    3: { cellWidth: 15 }, // Debit
                    4: { cellWidth: 15 }, // Credit
                    5: { cellWidth: 20 }  // Balance
                }
            });

            // Save the PDF
            pdf.save(`User_Report_${filters.userId}_${filters.fromDate}_to_${filters.toDate}.pdf`);

            toast.success("PDF report downloaded successfully");
        } catch (error) {
            console.error("Error generating PDF:", error);
            toast.error("Failed to generate PDF report");
        }
    };

    const addCardToPDF = (pdf, x, y, width, height, title, value) => {
        // Card background
        pdf.setFillColor(245, 245, 245);
        pdf.rect(x, y, width, height, 'F');

        // Card border
        pdf.setDrawColor(200, 200, 200);
        pdf.rect(x, y, width, height);

        // Card title
        pdf.setFontSize(10);
        pdf.setTextColor(100);
        pdf.text(title, x + 10, y + 20);

        // Card value
        pdf.setFontSize(16);
        pdf.setTextColor(0);
        const lines = value.split('\n');
        lines.forEach((line, i) => {
            pdf.text(line, x + 10, y + 40 + (i * 20));
        });
    };

    const handleDownloadCSV = () => {
        if (ledgerData.length === 0) {
            toast.warning("No data available to download");
            return;
        }

        // Prepare company and user details
        const companyDetails = [
            ["Company Name", `${TRADE_NAME}`],
            ["Application", `${APP_NAME}`],
            ["GST", `${GST_NUMBER || ''}`],
            ["Address", `${CONTACT_ADDRESS}`],
            ["Phone", `${CONTACT_NUMBER}`],
            ["Email", `${CONTACT_EMAIL}`],
            ["Website", `${WEBSITE}`],
            ["", ""]
        ];

        const userDetails = [
            ["User ID", userId || ''],
            ["From Date", filters.fromDate],
            ["To Date", filters.toDate],
            ["Download Date", new Date().toLocaleString()],
            ["", ""],
            ["", ""],
        ];

        // Prepare summary data
        const summaryDetails = [];
        if (summaryData) {
            summaryDetails.push(
                ["", ""],
                ["SUMMARY DATA", ""],
                ["Opening Balance", `₹${summaryData.openingBalance.toFixed(2)}`],
                ["Closing Balance", `₹${summaryData.closingBalance.toFixed(2)}`],
                ["Total Credit", `₹${summaryData.totalCredit.toFixed(2)}`],
                ["Total Debit", `₹${summaryData.totalDebit.toFixed(2)}`],
                ["Recharge Transactions", `${summaryData.recharge.count}`],
                ["Recharge Amount", `₹${summaryData.recharge.amount.toFixed(2)}`],
                ["Bill Payment Transactions", `${summaryData.billPayment.count}`],
                ["Bill Payment Amount", `₹${summaryData.billPayment.amount.toFixed(2)}`],
                ["", ""]
            );
        }

        // Prepare the ledger data
        const ledgerHeaders = [
            "Date",
            "Order ID",
            "Purpose",
            "Description",
            "Debit",
            "Credit",
            "Balance"
        ];

        const ledgerRows = ledgerData.map(entry => [
            entry.createAt,
            entry.order_id,
            `${getCategory(entry.category)} | ${entry.number}`,
            entry.description,
            entry.dr_amount !== "0" ? `₹${entry.dr_amount}` : '-',
            entry.cr_amount !== "0" ? `₹${entry.cr_amount}` : '-',
            `₹${entry.balance}`
        ]);

        // Combine all data
        const csvData = [
            ...companyDetails,
            ...userDetails,
            ...summaryDetails,
            ledgerHeaders,
            ...ledgerRows
        ];

        // Convert to CSV
        const csv = Papa.unparse({
            data: csvData,
            quotes: true,
            header: false
        });

        // Create a blob and download
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `ledger_${filters.fromDate}_to_${filters.toDate}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast.success("CSV downloaded successfully");
    };

    const getCategory = (category) => {
        switch (category) {
            case "1":
                return "Add Money";
            case "2":
                return "Mobile Recharge";
            case "3":
                return "Bill Payment";
            case "4":
                return "Bonus";
            case "5":
                return "Debit Money";
            case "6":
                return "Fund Request";
            case "7":
                return "Credit from Admin";
            case "8":
                return "Debit Adjustment";
            case "9":
                return "Credit Transfer";
            case "10":
                return "Debit Transfer";
            case "11":
                return "Payout to Bank Account";
            case "12":
                return "Settlement to Wallet";
            case "13":
                return "Credit Adjustment";
            case "14":
                return "SMS Charges";
            default:
                return "Other";
        }
    }

    // Prepare data for charts
    const transactionChartData = {
        labels: ['Credit', 'Debit'],
        datasets: [
            {
                label: 'Amount',
                data: [
                    summaryData?.totalCredit || 0,
                    summaryData?.totalDebit || 0
                ],
                backgroundColor: [
                    'rgba(75, 192, 192, 0.6)',
                    'rgba(255, 99, 132, 0.6)'
                ],
                borderColor: [
                    'rgba(75, 192, 192, 1)',
                    'rgba(255, 99, 132, 1)'
                ],
                borderWidth: 1
            }
        ]
    };

    const categoryChartData = {
        labels: summaryData ? Object.keys(summaryData.categories) : [],
        datasets: [
            {
                label: 'Transaction Count',
                data: summaryData ? Object.values(summaryData.categories).map(c => c.count) : [],
                backgroundColor: [
                    'rgba(255, 99, 132, 0.6)',
                    'rgba(54, 162, 235, 0.6)',
                    'rgba(255, 206, 86, 0.6)',
                    'rgba(75, 192, 192, 0.6)',
                    'rgba(153, 102, 255, 0.6)',
                    'rgba(255, 159, 64, 0.6)'
                ],
                borderColor: [
                    'rgba(255, 99, 132, 1)',
                    'rgba(54, 162, 235, 1)',
                    'rgba(255, 206, 86, 1)',
                    'rgba(75, 192, 192, 1)',
                    'rgba(153, 102, 255, 1)',
                    'rgba(255, 159, 64, 1)'
                ],
                borderWidth: 1
            }
        ]
    };

    // Apply filters to API data
    const applyFilters = () => {
        fetchLedgerData();
        setFilterDrawerOpen(false);
    };

    const resetFilters = () => {
        setFilters({
            userId: userId || '',
            fromDate: getTodayDate(),
            toDate: getTodayDate()
        });
    };

    return (

        <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-white">
            <div className="flex flex-col flex-1">
                <Header />
                <main className="flex-1 overflow-auto">
                    {/* Filter Drawer */}
                    <AnimatePresence>
                        {filterDrawerOpen && (
                            <motion.div
                                initial={{ x: '-100%' }}
                                animate={{ x: 0 }}
                                exit={{ x: '-100%' }}
                                transition={{ type: 'tween' }}
                                className="fixed inset-y-0 left-0 z-50 w-80 bg-white dark:bg-gray-800 shadow-lg overflow-y-auto"
                            >
                                <div className="p-4">
                                    <div className="flex items-center justify-between mb-4">
                                        <h2 className="text-lg font-medium">Filter Report</h2>
                                        <button
                                            onClick={() => setFilterDrawerOpen(false)}
                                            className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                                        >
                                            <XMarkIcon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                                        </button>
                                    </div>

                                    <div className="space-y-4">
                                        <div className="space-y-1">
                                            <label htmlFor="userId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                User ID
                                            </label>
                                            <input
                                                type="text"
                                                name="userId"
                                                id="userId"
                                                value={filters.userId}
                                                onChange={handleFilterChange}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-700"
                                                placeholder="Enter user ID"
                                            />
                                        </div>

                                        <div className="space-y-1">
                                            <label htmlFor="fromDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                From Date
                                            </label>
                                            <input
                                                type="date"
                                                name="fromDate"
                                                id="fromDate"
                                                value={filters.fromDate}
                                                onChange={handleFilterChange}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-700"
                                            />
                                        </div>

                                        <div className="space-y-1">
                                            <label htmlFor="toDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                To Date
                                            </label>
                                            <input
                                                type="date"
                                                name="toDate"
                                                id="toDate"
                                                value={filters.toDate}
                                                onChange={handleFilterChange}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-700"
                                            />
                                        </div>
                                    </div>

                                    <div className="mt-6 flex space-x-3">
                                        <button
                                            onClick={resetFilters}
                                            className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                                        >
                                            Reset
                                        </button>
                                        <button
                                            onClick={applyFilters}
                                            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                        >
                                            Apply Filters
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Overlay when filter drawer is open */}
                    {filterDrawerOpen && (
                        <div
                            className="fixed inset-0 bg-black bg-opacity-50 z-40"
                            onClick={() => setFilterDrawerOpen(false)}
                        />
                    )}

                    <main className="flex-1 p-6 overflow-auto">
                        <div className="max-w-7xl mx-auto">
                            <div className="flex items-center justify-between mb-6">
                                <button
                                    onClick={() => navigate(-1)}
                                    className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-600"
                                >
                                    <ChevronLeftIcon className="h-5 w-5 mr-1" />
                                    Back
                                </button>
                                <h2 className="text-xl font-semibold">
                                    Report Management
                                </h2>
                                <button
                                    onClick={() => setFilterDrawerOpen(true)}
                                    className="p-2 rounded-full hover:bg-gray-300 bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center"
                                >
                                    <TbFilterPause className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                                </button>
                            </div>

                            {/* Summary Cards */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                                {loading ? (
                                    <>
                                        <CardSkeleton />
                                        <CardSkeleton />
                                        <CardSkeleton />
                                        <CardSkeleton />
                                    </>
                                ) : summaryData ? (
                                    <>
                                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                                            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Opening Balance</h3>
                                            <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">
                                                ₹{summaryData.openingBalance.toFixed(2)}
                                            </p>
                                        </div>
                                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                                            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Closing Balance</h3>
                                            <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">
                                                ₹{summaryData.closingBalance.toFixed(2)}
                                            </p>
                                        </div>
                                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                                            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Credit</h3>
                                            <p className="mt-2 text-2xl font-semibold text-green-600 dark:text-green-400">
                                                ₹{summaryData.totalCredit.toFixed(2)}
                                            </p>
                                        </div>
                                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                                            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Debit</h3>
                                            <p className="mt-2 text-2xl font-semibold text-red-600 dark:text-red-400">
                                                ₹{summaryData.totalDebit.toFixed(2)}
                                            </p>
                                        </div>
                                    </>
                                ) : (
                                    <div className="col-span-4 text-center py-8 text-gray-500 dark:text-gray-400">
                                        No summary data available
                                    </div>
                                )}
                            </div>

                            {/* Transaction Type Cards */}
                            {summaryData && (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Recharge Transactions</h3>
                                        <div className="mt-2 flex justify-between items-end">
                                            <div>
                                                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                                                    {summaryData.recharge.count}
                                                </p>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">Total transactions</p>
                                            </div>
                                            <p className="text-lg font-medium text-blue-600 dark:text-blue-400">
                                                ₹{summaryData.recharge.amount.toFixed(2)}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Bill Payments</h3>
                                        <div className="mt-2 flex justify-between items-end">
                                            <div>
                                                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                                                    {summaryData.billPayment.count}
                                                </p>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">Total transactions</p>
                                            </div>
                                            <p className="text-lg font-medium text-blue-600 dark:text-blue-400">
                                                ₹{summaryData.billPayment.amount.toFixed(2)}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Fund Transfers</h3>
                                        <div className="mt-2 flex justify-between items-end">
                                            <div>
                                                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                                                    {summaryData.fundTransfer.count}
                                                </p>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">Total transactions</p>
                                            </div>
                                            <p className="text-lg font-medium text-blue-600 dark:text-blue-400">
                                                ₹{summaryData.fundTransfer.amount.toFixed(2)}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Charts Section */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Transaction Distribution</h3>
                                    <div id="transaction-chart" className="h-64">
                                        {summaryData ? (
                                            <Pie
                                                data={transactionChartData}
                                                options={{
                                                    responsive: true,
                                                    maintainAspectRatio: false,
                                                    plugins: {
                                                        legend: {
                                                            position: 'bottom',
                                                            labels: {
                                                                color: '#6B7280'
                                                            }
                                                        }
                                                    }
                                                }}
                                            />
                                        ) : (
                                            <div className="h-full flex items-center justify-center text-gray-500 dark:text-gray-400">
                                                No data available for chart
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Transaction Categories</h3>
                                    <div id="category-chart" className="h-64">
                                        {summaryData ? (
                                            <Bar
                                                data={categoryChartData}
                                                options={{
                                                    responsive: true,
                                                    maintainAspectRatio: false,
                                                    plugins: {
                                                        legend: {
                                                            display: false
                                                        }
                                                    },
                                                    scales: {
                                                        y: {
                                                            beginAtZero: true,
                                                            ticks: {
                                                                color: '#6B7280'
                                                            },
                                                            grid: {
                                                                color: '#E5E7EB'
                                                            }
                                                        },
                                                        x: {
                                                            ticks: {
                                                                color: '#6B7280'
                                                            },
                                                            grid: {
                                                                display: false
                                                            }
                                                        }
                                                    }
                                                }}
                                            />
                                        ) : (
                                            <div className="h-full flex items-center justify-center text-gray-500 dark:text-gray-400">
                                                No data available for chart
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Actions Bar */}
                            <div className="flex justify-between items-center mb-4">
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                    Showing {ledgerData.length} records
                                </div>
                                <div className="space-x-3">
                                    <button
                                        onClick={handleDownloadCSV}
                                        disabled={ledgerData.length === 0}
                                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                                        Download CSV
                                    </button>
                                    <button
                                        onClick={handleDownloadPDF}
                                        disabled={ledgerData.length === 0}
                                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                                        Download PDF Report
                                    </button>
                                </div>
                            </div>

                            {/* Ledger Table */}
                            <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                                {loading ? (
                                    <div className="p-6">
                                        <TableSkeleton />
                                    </div>
                                ) : ledgerData.length > 0 ? (
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                                            <thead className="bg-gray-50 dark:bg-gray-700">
                                                <tr>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                        Date
                                                    </th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                        Order ID
                                                    </th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                        Purpose
                                                    </th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                        Description
                                                    </th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                        Debit
                                                    </th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                        Credit
                                                    </th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                        Balance
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                                                {ledgerData.map((entry, index) => (
                                                    <tr key={index} className={index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700'}>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                            {entry.createAt}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                            {entry.order_id}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                            {getCategory(entry.category)} | {entry.number}
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                                                            {entry.description}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 dark:text-red-400">
                                                            {entry.dr_amount !== "0" ? `₹${entry.dr_amount}` : '-'}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 dark:text-green-400">
                                                            {entry.cr_amount !== "0" ? `₹${entry.cr_amount}` : '-'}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                            ₹{entry.balance}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="p-6 text-center">
                                        <p className="text-gray-500 dark:text-gray-400">
                                            No ledger data found. Apply filters to view records.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </main>
                </main>
            </div>
            <ToastContainer />
        </div>
    );
};

export default UserReport;